import type { NodeTypeDef } from '../core/types';

/** Tonal and colour adjustments. Every node here is 1-in, 1-out and pixel-local. */
export const adjustments: NodeTypeDef[] = [
  {
    id: 'adjust.levels',
    label: 'Levels',
    category: 'adjust',
    description: 'Remap input black/white points through gamma onto an output range.',
    guidance:
      'The primary contrast tool. Pulling the Input black and white points toward each other hardens a mask; Gamma shifts the midpoint without clipping either end. Almost every mask in a finished graph passes through one of these.',
    keywords: ['contrast', 'brightness', 'gamma', 'curve', 'range', 'remap'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      {
        name: 'inBlack',
        label: 'Black',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.001,
        track: { name: 'Input', handle: 'low', histogram: true },
      },
      {
        name: 'gamma',
        label: 'Gamma',
        type: 'float',
        default: 1,
        min: 0.01,
        max: 8,
        step: 0.01,
        // The shader raises to 1/gamma, so a larger value lifts the midtones.
        // This is the direction Photoshop's midtone handle moves in too.
        help: 'Above 1 brightens midtones, below 1 darkens them.',
        track: { name: 'Input', handle: 'mid' },
      },
      {
        name: 'inWhite',
        label: 'White',
        type: 'float',
        default: 1,
        min: 0,
        max: 1,
        step: 0.001,
        track: { name: 'Input', handle: 'high' },
      },
      {
        name: 'outBlack',
        label: 'Black',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.001,
        track: { name: 'Output', handle: 'low' },
      },
      {
        name: 'outWhite',
        label: 'White',
        type: 'float',
        default: 1,
        min: 0,
        max: 1,
        step: 0.001,
        track: { name: 'Output', handle: 'high' },
      },
      {
        name: 'channel',
        label: 'Channel',
        type: 'enum',
        default: 'all',
        options: [
          { value: 'all', label: 'All' },
          { value: 'red', label: 'Red' },
          { value: 'green', label: 'Green' },
          { value: 'blue', label: 'Blue' },
          { value: 'alpha', label: 'Alpha' },
        ],
        help: 'Restrict the remap to one channel. Chain three of these for independent RGB control.',
      },
      {
        name: 'invert',
        label: 'Invert',
        type: 'bool',
        default: false,
        help: 'Swaps the output points, which is cheaper than a separate Invert node.',
      },
    ],
    shader: /* glsl */ `
      float levelsOne(float v) {
        float span = p_inWhite - p_inBlack;
        // A collapsed input range is a hard threshold, not a divide-by-zero.
        float t = abs(span) < 1e-5 ? step(p_inBlack, v) : saturate((v - p_inBlack) / span);
        t = pow(t, 1.0 / max(p_gamma, 1e-4));
        return p_invert ? mix(p_outWhite, p_outBlack, t) : mix(p_outBlack, p_outWhite, t);
      }

      vec4 texdir(vec2 uv) {
        vec4 c = in0(uv);
        if (p_channel == K_channel_all) {
          return vec4(levelsOne(c.r), levelsOne(c.g), levelsOne(c.b), c.a);
        }
        if (p_channel == K_channel_red) return vec4(levelsOne(c.r), c.g, c.b, c.a);
        if (p_channel == K_channel_green) return vec4(c.r, levelsOne(c.g), c.b, c.a);
        if (p_channel == K_channel_blue) return vec4(c.r, c.g, levelsOne(c.b), c.a);
        return vec4(c.rgb, levelsOne(c.a));
      }
    `,
  },

  {
    id: 'adjust.curve',
    label: 'Curve',
    category: 'adjust',
    description: 'Remap tones through an arbitrary spline.',
    guidance:
      'Where Levels gives you three handles, a curve gives you as many as you need. Use it to lift shadows without touching highlights, to notch out one band of a noise, or to build an S-curve for contrast that does not clip. Applies to all channels, or to one at a time.',
    keywords: ['tone', 'spline', 'contrast', 'remap', 'levels', 'transfer', 's-curve'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      {
        name: 'curve',
        label: 'Curve',
        type: 'curve',
        default: [
          { x: 0, y: 0 },
          { x: 1, y: 1 },
        ],
      },
      {
        name: 'channel',
        label: 'Channel',
        type: 'enum',
        default: 'all',
        options: [
          { value: 'all', label: 'All' },
          { value: 'red', label: 'Red' },
          { value: 'green', label: 'Green' },
          { value: 'blue', label: 'Blue' },
          { value: 'alpha', label: 'Alpha' },
        ],
        help: 'Restrict the remap to one channel, leaving the rest untouched.',
      },
      {
        name: 'amount',
        label: 'Amount',
        type: 'float',
        default: 1,
        min: 0,
        max: 1,
        step: 0.001,
        group: 'Output',
        help: 'Blends between the original and the remapped result.',
      },
    ],
    shader: /* glsl */ `
      float applyCurve(float v) {
        // The spline is baked to a 256x1 lookup on the CPU, so evaluating it is
        // a single tap no matter how many control points it has.
        return texture(p_curve, vec2(saturate(v), 0.5)).r;
      }

      vec4 texdir(vec2 uv) {
        vec4 c = in0(uv);
        vec4 mapped = c;
        if (p_channel == K_channel_all) {
          mapped = vec4(applyCurve(c.r), applyCurve(c.g), applyCurve(c.b), c.a);
        } else if (p_channel == K_channel_red) {
          mapped.r = applyCurve(c.r);
        } else if (p_channel == K_channel_green) {
          mapped.g = applyCurve(c.g);
        } else if (p_channel == K_channel_blue) {
          mapped.b = applyCurve(c.b);
        } else {
          mapped.a = applyCurve(c.a);
        }
        return mix(c, mapped, p_amount);
      }
    `,
  },

  {
    id: 'adjust.histogramScan',
    label: 'Histogram Scan',
    category: 'adjust',
    description: 'Sweep a soft threshold across the tonal range.',
    guidance:
      'The cleanest way to turn a smooth gradient or noise into a mask. Animate Position to grow or shrink coverage; Contrast controls how hard the boundary is. Prefer this over Levels when you want a mask, not a tonal edit.',
    keywords: ['threshold', 'mask', 'position', 'coverage', 'select'],
    inputs: [{ name: 'input', label: 'Input', kind: 'grayscale' }],
    output: 'grayscale',
    params: [
      {
        name: 'position',
        label: 'Position',
        type: 'float',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.001,
      },
      {
        name: 'contrast',
        label: 'Contrast',
        type: 'float',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.001,
        help: '1 is a hard cut, 0 leaves the input almost untouched.',
      },
      { name: 'invert', label: 'Invert', type: 'bool', default: false, group: 'Output' },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        float v = in0g(uv);
        // Width shrinks toward zero as contrast approaches 1, so the ramp
        // tightens into a step without ever dividing by zero.
        float width = max(1.0 - p_contrast, 1e-4) * 0.5;
        float lo = p_position - width;
        float hi = p_position + width;
        float o = smoothstep(lo, hi, v);
        if (p_invert) o = 1.0 - o;
        return vec4(vec3(o), 1.0);
      }
    `,
  },

  {
    id: 'adjust.invert',
    label: 'Invert',
    category: 'adjust',
    description: 'Invert colour and/or alpha.',
    guidance: 'Trivial but constant: masks are wrong-way-round more often than not.',
    keywords: ['negative', 'flip', 'complement', 'one minus'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      { name: 'color', label: 'Invert Color', type: 'bool', default: true },
      { name: 'alpha', label: 'Invert Alpha', type: 'bool', default: false },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec4 c = in0(uv);
        if (p_color) c.rgb = 1.0 - c.rgb;
        if (p_alpha) c.a = 1.0 - c.a;
        return c;
      }
    `,
  },

  {
    id: 'adjust.hsl',
    label: 'HSL',
    category: 'adjust',
    description: 'Shift hue, saturation, and lightness.',
    guidance:
      'Recolour without rebuilding. Set Saturation to 0 to derive a clean grayscale mask from a colour map.',
    keywords: ['hue', 'saturation', 'lightness', 'recolor', 'tint', 'desaturate'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'color',
    params: [
      { name: 'hue', label: 'Hue Shift', type: 'float', default: 0, min: -1, max: 1, step: 0.001 },
      {
        name: 'saturation',
        label: 'Saturation',
        type: 'float',
        default: 1,
        min: 0,
        max: 4,
        step: 0.01,
      },
      {
        name: 'lightness',
        label: 'Lightness',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec4 c = in0(uv);
        vec3 hsl = rgbToHsl(saturate(c.rgb));
        hsl.x = fract(hsl.x + p_hue);
        hsl.y = saturate(hsl.y * p_saturation);
        hsl.z = saturate(hsl.z + p_lightness);
        return vec4(hslToRgb(hsl), c.a);
      }
    `,
  },

  {
    id: 'adjust.gradientMap',
    label: 'Gradient Map',
    category: 'adjust',
    description: 'Colourise a grayscale input through a gradient ramp.',
    guidance:
      'How a height or mask becomes base colour. Build your value structure in grayscale, then map it — editing the ramp restyles the whole material without touching the graph.',
    keywords: ['colorize', 'ramp', 'palette', 'lut', 'tint', 'basecolor'],
    inputs: [{ name: 'input', label: 'Input', kind: 'grayscale' }],
    output: 'color',
    params: [
      {
        name: 'position',
        label: 'Position',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
        group: 'Mapping',
        help: 'Slides the ramp along the input range.',
      },
      {
        name: 'repeat',
        label: 'Repeat',
        type: 'int',
        default: 1,
        min: 1,
        max: 32,
        group: 'Mapping',
        help: 'Cycles the ramp this many times across the range, giving contour bands.',
      },
      {
        name: 'mirror',
        label: 'Mirror Repeats',
        type: 'bool',
        default: false,
        group: 'Mapping',
        help: 'Reverses alternate cycles so repeats meet continuously instead of jumping.',
      },
      {
        name: 'gradient',
        label: 'Gradient',
        type: 'gradient',
        default: [
          { t: 0, color: [0.05, 0.05, 0.08, 1] },
          { t: 0.5, color: [0.5, 0.42, 0.35, 1] },
          { t: 1, color: [0.95, 0.92, 0.85, 1] },
        ],
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        float t = in0g(uv) + p_position;
        t *= float(p_repeat);
        if (p_mirror) {
          float m = mod(t, 2.0);
          t = min(m, 2.0 - m);
        } else if (p_repeat > 1) {
          t = fract(t);
        }
        // The ramp is baked to a 256×1 LUT on the CPU; sampling it is one tap.
        return texture(p_gradient, vec2(saturate(t), 0.5));
      }
    `,
  },

  {
    id: 'adjust.posterize',
    label: 'Posterize',
    category: 'adjust',
    description: 'Quantise values into discrete steps.',
    guidance:
      'Terracing for stylised looks, and a way to build stepped height for stacked strata or contour lines.',
    keywords: ['quantize', 'steps', 'terrace', 'banding', 'contour', 'toon'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      { name: 'steps', label: 'Steps', type: 'int', default: 4, min: 2, max: 64 },
      {
        name: 'smoothness',
        label: 'Smoothness',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        help: 'Softens each step boundary instead of hard-quantising.',
      },
    ],
    shader: /* glsl */ `
      float posterizeOne(float v) {
        float n = float(p_steps - 1);
        float scaled = v * n;
        float base = floor(scaled);
        float frac = scaled - base;
        // Smoothness reintroduces a controlled ramp inside each step.
        float w = max(p_smoothness, 1e-4) * 0.5;
        float eased = smoothstep(0.5 - w, 0.5 + w, frac);
        return saturate((base + eased) / n);
      }

      vec4 texdir(vec2 uv) {
        vec4 c = in0(uv);
        return vec4(posterizeOne(c.r), posterizeOne(c.g), posterizeOne(c.b), c.a);
      }
    `,
  },

  {
    id: 'adjust.autoLevels',
    label: 'Auto Levels',
    category: 'adjust',
    description: 'Stretch the input to fill the full range automatically.',
    guidance:
      'Normalises whatever it is given. Useful after operations whose output range depends on their inputs — a Distance field, a warped noise, a heavily filtered height — where the right Levels values are impossible to guess and change whenever an upstream parameter moves. Feeding this into Normal gives consistent relief no matter how the height was built.',
    keywords: ['normalize', 'stretch', 'auto contrast', 'equalize', 'range', 'histogram'],
    inputs: [{ name: 'input', label: 'Input', kind: 'grayscale' }],
    output: 'grayscale',
    params: [
      {
        name: 'amount',
        label: 'Amount',
        type: 'float',
        default: 1,
        min: 0,
        max: 1,
        step: 0.001,
        help: 'Blends between the original and the fully stretched result.',
      },
      {
        name: 'outBlack',
        label: 'Black',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.001,
        track: { name: 'Output', handle: 'low' },
      },
      {
        name: 'outWhite',
        label: 'White',
        type: 'float',
        default: 1,
        min: 0,
        max: 1,
        step: 0.001,
        track: { name: 'Output', handle: 'high' },
      },
    ],
    /**
     * A doubling-radius reduction, then one pass to apply the result.
     *
     * Every other node here is pixel-local, which is precisely why Auto Levels
     * needs machinery: the answer depends on the whole image. Each pass gathers
     * the running min and max from four points twice as far away as the last,
     * so after n passes every pixel holds the extremes of a 2^n neighbourhood.
     * Because the targets wrap, that neighbourhood covers the entire image once
     * the radius reaches half its width — 12 passes is enough for 2048².
     */
    passes: { kind: 'fixed', count: 14 },
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        // Channel 0 carries the running minimum, channel 1 the maximum.
        if (uPass == 0) {
          float v = in0g(uv);
          return vec4(v, v, 0.0, 1.0);
        }

        int reducePasses = uPassCount - 1;
        if (uPass < reducePasses) {
          vec4 acc = prev(uv);
          float radius = pow(2.0, float(uPass - 1));
          vec2 d = radius * uTexel;
          for (int y = -1; y <= 1; y++) {
            for (int x = -1; x <= 1; x++) {
              vec4 s = prev(uv + vec2(float(x), float(y)) * d);
              acc.r = min(acc.r, s.r);
              acc.g = max(acc.g, s.g);
            }
          }
          return acc;
        }

        vec4 range = prev(uv);
        float span = range.g - range.r;
        float v = in0g(uv);
        // A flat image has no range to stretch; leaving it alone beats
        // amplifying its noise to full scale.
        float stretched = span > 1e-4 ? (v - range.r) / span : v;
        float out0 = mix(p_outBlack, p_outWhite, saturate(stretched));
        return vec4(vec3(mix(v, out0, p_amount)), 1.0);
      }
    `,
  },

  {
    id: 'adjust.clamp',
    label: 'Clamp',
    category: 'adjust',
    description: 'Constrain values to a range, with optional wrapping.',
    guidance:
      'Housekeeping after additive blends that overshoot. Wrap mode instead folds the overshoot back, which produces banded psychedelic patterns from smooth input.',
    keywords: ['limit', 'range', 'saturate', 'wrap', 'fold'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      { name: 'min', label: 'Min', type: 'float', default: 0, min: -4, max: 4, step: 0.001 },
      { name: 'max', label: 'Max', type: 'float', default: 1, min: -4, max: 4, step: 0.001 },
      {
        name: 'mode',
        label: 'Mode',
        type: 'enum',
        default: 'clamp',
        options: [
          { value: 'clamp', label: 'Clamp' },
          { value: 'wrap', label: 'Wrap' },
          { value: 'fold', label: 'Fold' },
        ],
      },
    ],
    shader: /* glsl */ `
      float applyRange(float v) {
        float lo = min(p_min, p_max);
        float hi = max(p_min, p_max);
        float span = hi - lo;
        if (span < 1e-6) return lo;
        if (p_mode == K_mode_clamp) return clamp(v, lo, hi);
        if (p_mode == K_mode_wrap) return lo + mod(v - lo, span);
        float m = mod(v - lo, span * 2.0);
        return lo + min(m, span * 2.0 - m);
      }

      vec4 texdir(vec2 uv) {
        vec4 c = in0(uv);
        return vec4(applyRange(c.r), applyRange(c.g), applyRange(c.b), c.a);
      }
    `,
  },
];
